import React, { forwardRef, useMemo, useState } from "react";
import {
  ActivityIndicator,
  Pressable,
  StyleProp,
  StyleSheet,
  Text,
  TextInput,
  TextInputProps,
  View,
  ViewStyle,
} from "react-native";

type InputProps = TextInputProps & {
  label?: string;
  containerStyle?: StyleProp<ViewStyle>;
  inputWrapperStyle?: StyleProp<ViewStyle>; // ✅ optional
  inputStyle?: TextInputProps["style"];     // ✅ optional (cleaner than props.style)
};

export const Input = forwardRef<TextInput, InputProps>(function Input({
  label,
  containerStyle,
  inputWrapperStyle,
  inputStyle,
  multiline,
  numberOfLines,
  ...props
}: InputProps, ref) {
  const [focused, setFocused] = useState(false);

  const borderColor = useMemo(() => {
    if (focused) return "rgba(249,115,22,0.85)";
    return "rgba(251,146,60,0.28)";
  }, [focused]);

  const isMultiline = !!multiline;

  return (
    <View style={[styles.fieldWrap, containerStyle]}>
      {label ? <Text style={styles.label}>{label}</Text> : null}

      <View
        style={[
          styles.inputWrap,
          { borderColor },
          isMultiline ? styles.inputWrapMultiline : null,
          inputWrapperStyle,
        ]}
      >
        <TextInput
          ref={ref}
          {...props}
          multiline={multiline}
          numberOfLines={numberOfLines ?? (isMultiline ? 4 : 1)}
          onFocus={(e) => {
            setFocused(true);
            props.onFocus?.(e);
          }}
          onBlur={(e) => {
            setFocused(false);
            props.onBlur?.(e);
          }}
          placeholderTextColor="rgba(74,35,17,0.35)"
          textAlign={props.textAlign ?? "left"}
          textAlignVertical={isMultiline ? "top" : "center"} // ✅ ANDROID FIX
          style={[
            styles.input,
            isMultiline ? styles.inputMultiline : null,
            props.style,   // keep backward compatibility
            inputStyle,    // preferred override
          ]}
        />
      </View>
    </View>
  );
});

type ButtonProps = {
  title: string;
  onPress: () => void;
  disabled?: boolean;
  loading?: boolean;
  style?: StyleProp<ViewStyle>;
};

export function Button({ title, onPress, disabled, loading = false, style }: ButtonProps) {
  const isDisabled = disabled || loading;
  return (
    <Pressable
      onPress={onPress}
      disabled={isDisabled}
      style={({ pressed }) => [
        styles.btn,
        isDisabled ? { opacity: 0.55 } : null,
        pressed && !isDisabled ? { opacity: 0.92, transform: [{ scale: 0.99 }] } : null,
        style,
      ]}
    >
      {loading ? <ActivityIndicator size="small" color="#fff" /> : <Text style={styles.btnText}>{title}</Text>}
    </Pressable>
  );
}

const styles = StyleSheet.create({
  fieldWrap: { gap: 6 },

  label: {
    fontSize: 12,
    color: "rgba(74,35,17,0.70)",
    fontWeight: "700",
  },

  // ✅ default single-line
  inputWrap: {
    height: 44,
    borderRadius: 10,
    borderWidth: 1,
    backgroundColor: "rgba(255,255,255,0.75)",
    paddingHorizontal: 12,
    justifyContent: "center",
  },

  // ✅ multiline: allow height growth + top padding
  inputWrapMultiline: {
    height: "auto",
    minHeight: 90,
    paddingVertical: 10,
    justifyContent: "flex-start",
  },

  input: {
    fontSize: 14,
    color: "#4A2311",
    fontWeight: "600",
    paddingVertical: 0,
  },

  inputMultiline: {
    minHeight: 70,
    paddingTop: 0, // wrapper has padding already
  },

  btn: {
    height: 46,
    borderRadius: 10,
    backgroundColor: "#F97316",
    alignItems: "center",
    justifyContent: "center",
    shadowColor: "#000",
    shadowOpacity: 0.12,
    shadowRadius: 10,
    shadowOffset: { width: 0, height: 6 },
    elevation: 4,
  },

  btnText: { color: "#fff", fontWeight: "800", fontSize: 14 },
});
